export const Role = {
  Admin: 'ADMIN',
  User: 'USER'
} as const

export type Role = (typeof Role)[keyof typeof Role]

/** The authenticated user, as `/api/auth/me` returns it. Never includes secrets. */
export interface UserDTO {
  id: string
  email: string
  name: string
  role: Role
  isActive: boolean
  mustChangePassword: boolean
  createdAt: string
  lastLoginAt: string | null
}

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  name: string
  password: string
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

/** Shape of `/api/auth/session` — lets the SPA boot without a 401 round-trip. */
export interface SessionDTO {
  user: UserDTO | null
  /** True when the instance has no users yet, so the UI can offer first-run setup. */
  needsBootstrap: boolean
  /** Whether self-service registration is enabled instance-wide. */
  allowRegistration: boolean
  /**
   * When the current session dies — and with it every connected site, image and
   * alt text. Null when unauthenticated. The UI uses this to warn before the work
   * is destroyed, which matters because nothing here is persisted.
   */
  lifetime: SessionLifetimeDTO | null
}

/**
 * Session expiry, in both forms.
 *
 * `expiresAt` is the absolute ceiling. `idleExpiresAt` slides forward on activity —
 * including a heartbeat from an open tab, so work a user is actively looking at is
 * never swept out from under them.
 */
export interface SessionLifetimeDTO {
  expiresAt: string
  idleExpiresAt: string
  ttlMs: number
  idleTtlMs: number
}

/** An SSH key as returned by the API — private material is never included. */
export interface SshKeyDTO {
  id: string
  label: string
  keyType: string
  fingerprint: string
  publicKey: string | null
  createdAt: string
  lastUsedAt: string | null
  /** How many sites currently reference this key. */
  siteCount: number
  /**
   * True for the one key designated as this instance's WP Engine key. Every user's
   * sites connect through it and regular users never see the keys UI at all, so it
   * is the only key most installs will ever have.
   */
  isInstanceKey: boolean
}

export interface CreateSshKeyRequest {
  label: string
  /** OpenSSH-format private key, PEM text. Encrypted at rest, never echoed back. */
  privateKey: string
  passphrase?: string
}
